import React, {
	useEffect, useState, useCallback, useMemo,
} from 'react';
import { merge, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import useStyles from 'isomorphic-style-loader/useStyles';
import { Container } from '../../../../symphony';
import { ILanguageType } from '../../constants/LanguageTypes';
import { SlDataService } from '../../services/sl-data.service';
import CodeLoader from '../../components/Loader/CodeLoader/CodeLoader';
import { EditorWithLabel } from '../../components/EditorWithLabel/EditorWithLabel';
import { OutputTextContainer } from '../../components/Output/OutputTextContainer';
import { OutputIframeContainer } from '../../components/Output/OutputIframeContainer';
import { Orientation } from '../../../../shared/public/SlCodeEditorComponents/types/types';
import { ICollapsedPanels, SplitPane } from '../../../../shared/public/SlCodeEditorComponents/SplitPane/SplitPane';
import { Panel } from '../../../../shared/public/SlCodeEditorComponents/Panel/Panel';
import { CollapsedView } from '../../../../shared/public/SlCodeEditorComponents/CollapsedView/CollapsedView';
import { CodeLanguages } from '../../../../api/private/global.interface';
import { PlaygroundOutputType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { OutputHeader } from '../../../../shared/public/SlCodeEditorComponents/OutputHeader/OutputHeader';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlEventsService } from '../../services/Business/sl-events.service';
import { lePlaygroundContext } from '../../global-constants';

import s from './NotWeb.scss';

export interface INotWebProps {
	languageType: ILanguageType;
}

export function NotWeb({ languageType }: INotWebProps): JSX.Element {
	useStyles(s);
	const [collapsedPanels, setCollapsedPanels] = useState<ICollapsedPanels>(
		{},
	);
	const [collapsesStates, setCollapsesStates] = useState({});
	const [isResizing, setIsResizing] = useState<boolean>(false);

	const { sourceCode$: sourceCode, isDarkMode$: isDarkMode, output$: output } = useContainerData(lePlaygroundContext.value, SlDataService, ['sourceCode$', 'isDarkMode$', 'output$']);

	const eventService = Container.take(lePlaygroundContext.value, SlEventsService);

	const { showConfirmLeaveAlert$, isResizingPanel$ } = Container.take(lePlaygroundContext.value, SlDataService);

	useEffect(() => {
		const unsubscriber = new Subject<void>();

		merge(
			isResizingPanel$.pipe(tap(setIsResizing)),
		)
			.pipe(takeUntil(unsubscriber))
			.subscribe();

		setCollapsesStates({});

		return () => {
			unsubscriber.next();
			unsubscriber.complete();
		};
	}, []);

	const onChange = (value: string, lng: CodeLanguages) => {
		if (!showConfirmLeaveAlert$.value) {
			showConfirmLeaveAlert$.next(true);
		}

		eventService.onNotWebCodeChange(value, lng);
	};

	const handleResize = useCallback(
		(sizes) => {
			let mergeState = collapsesStates;
			let collapseStateChange = false;
			const MIN_WIDTH = 48;

			sizes.forEach((size) => {
				if (size.width <= MIN_WIDTH) {
					if (!mergeState[size.id]) {
						collapseStateChange = true;
						mergeState = {
							...mergeState,
							[size.id]: true,
							size,
						};
					}
					return;
				}
				if (size.width > MIN_WIDTH) {
					if (mergeState[size.id]) {
						collapseStateChange = true;
						mergeState = {
							...mergeState,
							[size.id]: false,
							size,
						};
					}
				}
			});
			if (collapseStateChange) {
				setCollapsesStates(mergeState);
			}
		},
		[collapsesStates],
	);

	const onMouseDown = (): void => {
		isResizingPanel$.next(true);
	};

	const onMouseUp = (): void => {
		isResizingPanel$.next(false);
	};

	const onCollapseClick = (id: string, orientation: Orientation) => (): void => {
		setCollapsedPanels({
			state: true,
			currentId: id,
			[id]: true,
			orientation,
		});
		setCollapsesStates({});
	};

	const callbackCollapseClicked = (id) => {
		setCollapsedPanels({
			state: false,
			currentId: id,
			[id]: false,
			orientation: 'horizontal',
		});
	};

	const isVisibleOutFrame = useMemo(() => (
		output?.outputType === PlaygroundOutputType.Web
            || languageType?.language === CodeLanguages.SQL
	), [output]);

	const { expandedTIYEditorRefs } = useContainerData(lePlaygroundContext.value, SlDataService, ['expandedTIYEditorRefs']);

	return (
		<SplitPane
			horizontal
			onResize={handleResize}
			onMouseUp={onMouseUp}
			onMouseDown={onMouseDown}
			collapsedPanels={collapsedPanels}
			callbackCollapseClicked={callbackCollapseClicked}
			defaultWidth={50}
			className="split-pane__horizontal"
		>
			<Panel width="50%" isResizing={isResizing} isDarkMode={isDarkMode}>
				<div className="panel-wrapper">
					<CollapsedView
						isDarkMode={isDarkMode}
						title={languageType?.label}
						show={collapsesStates['0']}
						onClick={onCollapseClick('0', 'horizontal')}
					/>
					<EditorWithLabel
						codeEditorRef={expandedTIYEditorRefs[0]}
						label={languageType?.label}
						language={languageType?.language}
						value={sourceCode}
						onChange={onChange}
						isDarkMode={isDarkMode}
					/>
					<CodeLoader />
				</div>
			</Panel>
			<Panel width="50%" isResizing={isResizing} isDarkMode={isDarkMode}>
				<div className="sl-not-web-wrapper__output">
					<OutputHeader title="OUTPUT" />
					<div
						className={`sl-not-web-wrapper__output__iframe${
							!isVisibleOutFrame ? ' non-visible' : ''
						}`}
					>
						<OutputIframeContainer
							value={output}
							isDarkMode={isDarkMode}
						/>
					</div>
					{!isVisibleOutFrame && (
						<OutputTextContainer
							value={output}
							isDarkMode={isDarkMode}
						/>
					)}
				</div>
			</Panel>
		</SplitPane>
	);
}
